import { useTranslation } from "react-i18next";
import { FoodsSelectServerPagination } from "@foodpilot/foods";
import { Box, FormControl } from "@mui/material";
import { useAllUsers } from "src/api/client-api/user/useAllUsers";

import { useDelayedSearch } from "src/utils/useDelayedSearch.tsx";
import type { SupplierFormAppearance } from "../../SupplierForm.types";
import { FoodsSelectTriggerAdapter } from "src/components/EntityForm/FoodsSelectTriggerAdapter";

type UserInterface = {
    "@id": IRI;
    firstname: string;
    lastname: string;
};

type SearchUserBarProps = {
    appearance?: SupplierFormAppearance;
    addUser: (newUser: UserInterface) => void;
    selectedUsers: UserInterface[];
};
export const SearchUserBar = (props: SearchUserBarProps) => {
    const { t } = useTranslation();
    const { addUser, appearance = "legacy", selectedUsers } = props;
    const { search, delayedSearch, setSearch } = useDelayedSearch();
    const { users } = useAllUsers({ search: delayedSearch, itemsPerPage: 100 });

    const selectedUsersId = selectedUsers.map((item) => item["@id"]);
    const unselectedUsers = users?.filter((user) => !selectedUsersId.includes(user["@id"])) ?? [];
    const isSearchFound = (value: string) => {
        if (delayedSearch === "") {
            return true;
        }

        return value.toLowerCase().includes(delayedSearch.toLowerCase());
    };

    const select = (
        <FoodsSelectServerPagination
            selectedOption={null}
            options={unselectedUsers}
            onChange={(selectedUser) => addUser(selectedUser as UserInterface)}
            getId={(user) => user.id}
            getName={(user) => `${user.firstname} ${user.lastname}`}
            closeOnSelect={false}
            textOptions={{
                placeholder: appearance === "legacy" ? t("component.select.placeholder") : t("users.search"),
                searchPlaceholder: t("component.select.searchplaceholder"),
                emptyOptionsText: t("component.select.nooptions"),
                defaultValueText: t("component.select.defaultvalue"),
                noResult: t("component.select.noresultfound"),
            }}
            search={search}
            setSearch={setSearch}
            isSearchFound={isSearchFound}
            BoxProps={appearance === "product-v2" ? { width: "100%" } : undefined}
        />
    );

    if (appearance === "legacy") {
        return <FormControl size="small">{select}</FormControl>;
    }

    return (
        <Box sx={{ width: "100%" }}>
            <FoodsSelectTriggerAdapter accessibleLabel={t("users.search", "Search users")}>
                {select}
            </FoodsSelectTriggerAdapter>
        </Box>
    );
};
